import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import { fill } from '../copy';
import type { InitialDataCopy } from '../initial-data-copy';

type ResumeOfferProps = {
  copy: InitialDataCopy;
  sectorName: string;
  languageName: string;
  answeredCount: number | null;
  totalCount: number | null;
  skippedCount: number;
  pausedAt: string;
  locale: string;
  onResume: () => void;
  onRestart: () => void;
};

function formatDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) return '';
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

/** Offer to pick a paused session up again, shown on the setup screen. */
export function ResumeOffer(props: ResumeOfferProps) {
  const { copy } = props;
  const pausedOn = formatDate(props.pausedAt, props.locale);
  const details = [
    props.totalCount ? fill(copy.resumeProgress, { answered: props.answeredCount ?? 0, total: props.totalCount }) : '',
    props.skippedCount > 0 ? fill(copy.resumeSkipped, { count: props.skippedCount }) : '',
  ].filter(Boolean);
  const progress = props.totalCount ? Math.min(100, Math.round(((props.answeredCount ?? 0) / props.totalCount) * 100)) : 0;

  return (
    <div className="rounded-lg border border-info/25 bg-info/[0.06] p-4" data-testid="initial-data-resume">
      <div className="flex items-start gap-3">
        <Icon name="history" className="mt-0.5 size-4 shrink-0 text-info" />
        <div className="min-w-0 flex-1 space-y-1 text-sm">
          <p className="font-medium text-foreground">{copy.resumeTitle}</p>
          <p className="truncate text-muted-foreground">{[props.sectorName, props.languageName].filter(Boolean).join(' · ')}</p>
          {details.length > 0 && <p className="text-xs text-muted-foreground">{details.join(' · ')}</p>}
          {pausedOn && <p className="text-xs text-faint-foreground">{fill(copy.resumePausedOn, { date: pausedOn })}</p>}
        </div>
      </div>

      {props.totalCount ? (
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-surface-2">
          <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
        </div>
      ) : null}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={props.onRestart} data-testid="initial-data-restart" className="h-11 w-full sm:w-auto">
          <Icon name="refreshCw" />
          {copy.restart}
        </Button>
        <Button type="button" onClick={props.onResume} data-testid="initial-data-resume-button" className="h-11 w-full sm:w-auto">
          <Icon name="play" />
          {copy.resume}
        </Button>
      </div>
    </div>
  );
}
